import { useAuth } from "@clerk/react";
import { useState, useCallback, useMemo } from "react";
import { useParams } from "wouter";
import { getContextualActionsForPost } from "scantling-common";
import {
  useApi,
  useListPosts,
  useListItemVersions,
  useListActions,
  useListUsers,
  useInvalidateLists,
  ItemSlug,
  DateDisplay,
  CountdownDisplay,
  IntegrationSlug,
  PhaseChip,
} from "./components.js";
import { NiceNameEditor } from "./nice_name_editor.js";
import { Actor } from "./actor.js";
import { HelmLayout } from "./layouts.js";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PencilSimple, X, Check } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { ContextualActionsWidget } from "./contextual_actions_widget.js";

function PostPageContent() {
  const { postSlug } = useParams<{ postSlug: string }>();
  const { getToken } = useAuth();
  const pool = useApi();
  const postsResult = useListPosts(`slug:${postSlug}`);
  const post = postsResult.data?.entries[0] ?? null;
  const itemsResult = useListItemVersions(
    post ? `slug:${post.itemSlug}` : undefined,
  );
  const item =
    itemsResult.data && itemsResult.data.entries.length > 0
      ? itemsResult.data.entries.reduce((a, b) =>
          a.version > b.version ? a : b,
        )
      : null;
  const invalidate = useInvalidateLists();
  const actionsResult = useListActions(
    `verb:createPost success:true dataSlug:${postSlug}`,
  );
  const usersResult = useListUsers();
  const users = useMemo(
    () => new Map((usersResult.data?.entries ?? []).map(u => [u.id, u])),
    [usersResult.data],
  );
  const creator = actionsResult.data?.entries[0]?.actor ?? null;
  const [error, setError] = useState<string | null>(null);
  const [editingScheduledTime, setEditingScheduledTime] = useState(false);
  const [scheduledTimeValue, setScheduledTimeValue] = useState("");

  const loading =
    postsResult.isLoading || (post !== null && itemsResult.isLoading);
  const loadError =
    postsResult.error ??
    (post === null && !postsResult.isLoading ? "Post not found" : null);

  const handlePostActionSuccess = useCallback(
    (action: { verb: string }) => {
      invalidate("listPosts");
      if (action.verb === "deletePost") {
        window.history.back();
      }
    },
    [invalidate],
  );

  const handleEditScheduledTime = useCallback(() => {
    if (post?.scheduledAt) {
      // Convert ISO string to datetime-local format (YYYY-MM-DDTHH:mm)
      const date = new Date(post.scheduledAt);
      const localDateTime = new Date(
        date.getTime() - date.getTimezoneOffset() * 60000,
      )
        .toISOString()
        .slice(0, 16);
      setScheduledTimeValue(localDateTime);
    } else {
      setScheduledTimeValue("");
    }
    setEditingScheduledTime(true);
  }, [post?.scheduledAt]);

  const handleSaveScheduledTime = useCallback(async () => {
    if (!postSlug) return;
    const api = pool.acquire();
    try {
      const token = await getToken();
      if (!token) return;
      const authedApi = await api.authenticate(token);
      const orgApi = authedApi.org;
      if (!orgApi) return;
      // Convert datetime-local to ISO string, or null if empty
      const scheduledAt = scheduledTimeValue
        ? new Date(scheduledTimeValue).toISOString()
        : null;
      await orgApi.executeAction({
        verb: "updatePostScheduledTime",
        params: { postSlug, scheduledAt },
      });
      invalidate("listPosts");
      setEditingScheduledTime(false);
    } catch (err) {
      setError(`Failed to update scheduled time: ${err}`);
    } finally {
      pool.release();
    }
  }, [pool, getToken, postSlug, scheduledTimeValue, invalidate]);

  const handleCancelScheduledTimeEdit = useCallback(() => {
    setEditingScheduledTime(false);
    setScheduledTimeValue("");
  }, []);

  const handleSaveNiceName = useCallback(
    async (niceName: string | null) => {
      if (!postSlug) return;
      const api = pool.acquire();
      try {
        const token = await getToken();
        if (!token) return;
        const authedApi = await api.authenticate(token);
        const orgApi = authedApi.org;
        if (!orgApi) return;
        await orgApi.executeAction({
          verb: "updatePostNiceName",
          params: { postSlug, niceName },
        });
        invalidate("listPosts");
      } catch (err) {
        setError(`Failed to update name: ${err}`);
      } finally {
        pool.release();
      }
    },
    [pool, getToken, postSlug, invalidate],
  );

  if (loading) {
    return <div className="text-muted-foreground">Loading...</div>;
  }

  if (error || loadError) {
    return <div className="text-destructive">{error ?? loadError}</div>;
  }

  if (!post) {
    return <div className="text-muted-foreground">Post not found.</div>;
  }

  return (
    <div className="grid grid-cols-3 gap-4">
      <div className="col-span-2 flex flex-col gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <NiceNameEditor
                niceName={post.niceName}
                slug={post.slug}
                onSave={handleSaveNiceName}
              />
              {(() => {
                const actions = getContextualActionsForPost(post);
                return (
                  <ContextualActionsWidget
                    actions={actions}
                    onActionSuccess={handlePostActionSuccess}
                  />
                );
              })()}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-1">
                <span className="text-xs text-muted-foreground">Scheduled</span>
                {editingScheduledTime ? (
                  <div className="flex items-center gap-1">
                    <Input
                      type="datetime-local"
                      value={scheduledTimeValue}
                      onChange={e => setScheduledTimeValue(e.target.value)}
                      className="h-7 w-auto"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={handleSaveScheduledTime}
                    >
                      <Check size={14} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={handleCancelScheduledTimeEdit}
                    >
                      <X size={14} />
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-1">
                    <span
                      className={`py-0.5 text-sm ${post.scheduledAt ? "" : "text-muted-foreground italic"}`}
                    >
                      <DateDisplay
                        value={post.scheduledAt}
                        withTime
                        fallback="Not scheduled"
                      />
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={handleEditScheduledTime}
                    >
                      <PencilSimple size={12} />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Linked Item</CardTitle>
            <CardDescription>
              Content from{" "}
              <ItemSlug
                slug={post.itemSlug}
                version={post.itemVersion}
                item={item ?? undefined}
              />{" "}
              (version {post.itemVersion})
            </CardDescription>
          </CardHeader>
          <CardContent>
            {item ? (
              <div className="space-y-4">
                {item.niceName && (
                  <div>
                    <span className="text-xs text-muted-foreground">Name</span>
                    <p className="text-sm">{item.niceName}</p>
                  </div>
                )}
                <div>
                  <span className="text-xs text-muted-foreground">Phase</span>
                  <p className="text-sm">{item.phase}</p>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Slots</span>
                  <ul className="mt-1 space-y-1">
                    {item.slots.map(slot => (
                      <li
                        key={slot.name}
                        className="flex items-center justify-between text-sm"
                      >
                        <span>{slot.name}</span>
                        <span className="text-xs text-muted-foreground">
                          {slot.kind}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                Item not found or loading...
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="col-span-1 flex flex-col gap-4">
        <Card>
          <CardHeader>
            <CardTitle>Details</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="flex flex-col gap-3 text-xs">
              <div className="flex flex-col gap-1">
                <dt className="text-muted-foreground">Phase</dt>
                <dd>
                  <PhaseChip phase={post.phase} />
                </dd>
              </div>
              <div className="flex flex-col gap-1">
                <dt className="text-muted-foreground">Channel</dt>
                <dd className="text-sm">
                  <IntegrationSlug slug={post.channelSlug} />
                </dd>
              </div>
              <div className="flex flex-col gap-1">
                <dt className="text-muted-foreground">Creator</dt>
                <dd>
                  <Actor actor={creator} users={users} />
                </dd>
              </div>
              <div className="flex flex-col gap-1">
                <dt className="text-muted-foreground">Created</dt>
                <dd className="text-sm">
                  <DateDisplay value={post.createdAt} withTime />
                </dd>
              </div>
              {post.scheduledAt && (
                <div className="flex flex-col gap-1">
                  <dt className="text-muted-foreground">Countdown</dt>
                  <dd className="text-sm">
                    <CountdownDisplay value={post.scheduledAt} />
                  </dd>
                </div>
              )}
              <div className="flex flex-col gap-1">
                <dt className="text-muted-foreground">Item Version</dt>
                <dd className="text-sm">
                  <ItemSlug
                    slug={post.itemSlug}
                    version={post.itemVersion}
                    item={item ?? undefined}
                  />
                </dd>
              </div>
            </dl>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export const PostPage = () => {
  return (
    <HelmLayout>
      <PostPageContent />
    </HelmLayout>
  );
};
